// Accessibility utility functions and hooks
import { useEffect } from 'react';

/**
 * Keyboard navigation handler for interactive elements
 * @param {Function} onEnter - Callback when Enter key is pressed
 * @param {Function} onSpace - Callback when Space key is pressed
 * @param {Function} onEscape - Callback when Escape key is pressed
 */
export const useKeyboardNavigation = (onEnter, onSpace, onEscape) => {
  const handleKeyDown = (e) => {
    switch (e?.key) {
      case 'Enter':
        if (onEnter) {
          e?.preventDefault();
          onEnter(e);
        }
        break;
      case ' ':
        if (onSpace) {
          e?.preventDefault();
          onSpace(e);
        }
        break;
      case 'Escape':
        if (onEscape) {
          e?.preventDefault();
          onEscape(e);
        }
        break;
      default:
        break;
    }
  };

  return handleKeyDown;
};

/**
 * Focus trap for modals and dialogs
 * @param {React.RefObject} containerRef - Reference to container element
 * @param {boolean} isActive - Whether focus trap is active
 */
export const useFocusTrap = (containerRef, isActive) => {
  useEffect(() => {
    if (!isActive || !containerRef?.current) return;

    const container = containerRef?.current;
    const focusableElements = container?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
    );
    const firstElement = focusableElements?.[0];
    const lastElement = focusableElements?.[focusableElements?.length - 1];

    const handleTabKey = (e) => {
      if (e?.key !== 'Tab') return;

      if (e?.shiftKey) {
        if (document?.activeElement === firstElement) {
          e?.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document?.activeElement === lastElement) {
          e?.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container?.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      container?.removeEventListener('keydown', handleTabKey);
    };
  }, [containerRef, isActive]);
};

/**
 * Announce content to screen readers
 * @param {string} message - Message to announce
 * @param {string} priority - 'polite' or 'assertive'
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const announcement = document?.createElement('div');
  announcement?.setAttribute('role', 'status');
  announcement?.setAttribute('aria-live', priority);
  announcement?.setAttribute('aria-atomic', 'true');
  announcement?.classList?.add('sr-only');
  announcement.textContent = message;

  document?.body?.appendChild(announcement);

  setTimeout(() => {
    document?.body?.removeChild(announcement);
  }, 1000);
};

/**
 * Generate unique ID for accessibility attributes
 * @param {string} prefix - Prefix for the ID
 */
export const generateA11yId = (prefix = 'a11y') => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Skip to main content link handler
 */
export const skipToMainContent = () => {
  const mainContent = document?.querySelector('main') || document?.querySelector('[role="main"]');
  if (mainContent) {
    mainContent?.setAttribute('tabindex', '-1');
    mainContent?.focus();
    mainContent?.removeAttribute('tabindex');
  }
};

/**
 * Get ARIA label for common UI elements
 * @param {string} type - Type of element
 * @param {Object} context - Context data for label generation
 */
export const getAriaLabel = (type, context = {}) => {
  const labels = {
    'close-button': 'Close',
    'menu-button': context?.isOpen ? 'Close menu' : 'Open menu',
    'search-button': 'Search',
    'filter-button': 'Filter options',
    'sort-button': 'Sort options',
    'edit-button': `Edit ${context?.item || 'item'}`,
    'delete-button': `Delete ${context?.item || 'item'}`,
    'save-button': 'Save changes',
    'cancel-button': 'Cancel',
    'next-button': 'Next',
    'previous-button': 'Previous',
    'expand-button': context?.isExpanded ? 'Collapse' : 'Expand',
    'refresh-button': 'Refresh data'
  };

  return labels?.[type] || '';
};

/**
 * Keyboard navigation constants
 */
export const KEYBOARD_KEYS = {
  ENTER: 'Enter',
  SPACE: ' ',
  ESCAPE: 'Escape',
  TAB: 'Tab',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  HOME: 'Home',
  END: 'End'
};

export default {
  useKeyboardNavigation,
  useFocusTrap,
  announceToScreenReader,
  generateA11yId,
  skipToMainContent,
  getAriaLabel,
  KEYBOARD_KEYS
};